import {
  CUKCUK_INVOICE_COLUMNS as INV,
  type CukcukInvoiceLineItem,
  type MenuOption,
} from '../types/cukcukImport';
import type { InvoiceSelectedOption } from '../types/invoiceNotification';
import { buildCukcukOptionCode } from './cukcukParser';
import {
  mapCukcukOptionWithScore,
  OptionCatalogIndex,
  type OptionMatchMethod,
} from './optionMatcher';

const normalizeCode = (value: unknown): string => String(value ?? '').trim();

const toNumber = (value: unknown): number => {
  if (value === undefined || value === null || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isMainItemRow = (row: unknown[]): boolean => !!normalizeCode(row[INV.ITEM_CODE]);

const isOptionItemRow = (row: unknown[]): boolean => {
  const itemCode = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  return !itemCode && !!itemName;
};

const isSkippableInvoiceRow = (row: unknown[]): boolean => {
  const invoiceCode = normalizeCode(row[INV.INVOICE_CODE]);
  const itemCode = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  return !invoiceCode || (!itemCode && !itemName);
};

const parseMainInvoiceLineItem = (row: unknown[], rowNumber: number): CukcukInvoiceLineItem => {
  const itemId = normalizeCode(row[INV.ITEM_CODE]);
  const itemName = String(row[INV.ITEM_NAME] ?? '').trim();
  const unit = String(row[INV.UNIT] ?? '').trim();
  const itemGroup = String(row[INV.MENU_GROUP] ?? '').trim();
  const itemType = String(row[INV.ITEM_TYPE] ?? '').trim();
  const vatRate = String(row[INV.VAT_RATE] ?? '').trim();

  return {
    itemId,
    itemName,
    quantity: toNumber(row[INV.QUANTITY]) || 1,
    unit: unit || undefined,
    unitPrice: toNumber(row[INV.UNIT_PRICE]),
    lineTotal: toNumber(row[INV.LINE_TOTAL]),
    itemGroup: itemGroup || undefined,
    itemType: itemType || undefined,
    vatRate: vatRate || undefined,
    selectedOptions: [],
    sourceRowNumber: rowNumber,
  };
};

const parseInvoiceSelectedOption = (row: unknown[], parentItemId?: string): InvoiceSelectedOption => {
  const optionName = String(row[INV.ITEM_NAME] ?? '').trim();
  const quantity = toNumber(row[INV.QUANTITY]);
  const unitPrice = toNumber(row[INV.UNIT_PRICE]);
  const lineTotal = toNumber(row[INV.LINE_TOTAL]);
  const unit = String(row[INV.UNIT] ?? '').trim();

  return {
    optionId: buildCukcukOptionCode(optionName, parentItemId),
    optionName,
    quantity: quantity > 0 ? quantity : undefined,
    unit: unit || undefined,
    price: unitPrice > 0 ? unitPrice : undefined,
    amount: lineTotal > 0 ? lineTotal : unitPrice > 0 ? unitPrice : undefined,
  };
};

export interface MappedInvoiceSelectedOption extends InvoiceSelectedOption {
  mappedOption: MenuOption | null;
  matchScore?: number;
  matchMethod?: OptionMatchMethod;
}

export interface MappedCukcukInvoiceLineItem extends Omit<CukcukInvoiceLineItem, 'selectedOptions'> {
  selectedOptions: MappedInvoiceSelectedOption[];
}

export interface MapInvoiceOptionsResult {
  items: MappedCukcukInvoiceLineItem[];
  warnings: string[];
  totalOptions: number;
}

const mapSelectedOption = (
  option: InvoiceSelectedOption,
  catalog: OptionCatalogIndex,
  warnings: string[],
  rowNumber: number,
  parentItemId?: string,
): MappedInvoiceSelectedOption => {
  const match = mapCukcukOptionWithScore(option.optionName, catalog, parentItemId);

  if (!match.option) {
    warnings.push(
      `Dòng ${rowNumber}: không map được tùy chọn "${option.optionName}" sang catalog hiện có.`,
    );
  }

  return {
    ...option,
    optionId: match.option?.code ?? option.optionId,
    mappedOption: match.option,
    matchScore: match.score,
    matchMethod: match.method,
  };
};

/**
 * Gom dòng hóa đơn CUKCUK thành món chính + tùy chọn, rồi map sang catalog qua `mapCukcukOption`.
 */
export function mapInvoiceOptions(invoiceRows: unknown[][]): MapInvoiceOptionsResult;
export function mapInvoiceOptions(
  invoiceRows: unknown[][],
  existingOptions: MenuOption[],
): MapInvoiceOptionsResult;
export function mapInvoiceOptions(
  invoiceRows: unknown[][],
  existingOptions: MenuOption[] = [],
): MapInvoiceOptionsResult {
  const catalog = new OptionCatalogIndex(existingOptions);
  const items: MappedCukcukInvoiceLineItem[] = [];
  const warnings: string[] = [];
  let totalOptions = 0;

  invoiceRows.forEach((row, rowIndex) => {
    if (isSkippableInvoiceRow(row)) return;

    if (isMainItemRow(row)) {
      items.push({
        ...parseMainInvoiceLineItem(row, rowIndex + 1),
        selectedOptions: [],
      });
      return;
    }

    if (!isOptionItemRow(row)) return;

    const parentItem = items[items.length - 1];
    const optionName = String(row[INV.ITEM_NAME] ?? '').trim();

    if (!parentItem) {
      warnings.push(
        `Dòng ${rowIndex + 1}: tùy chọn "${optionName}" không có món chính trên hóa đơn.`,
      );
      return;
    }

    const parsedOption = parseInvoiceSelectedOption(row, parentItem.itemId);
    parentItem.selectedOptions.push(
      mapSelectedOption(parsedOption, catalog, warnings, rowIndex + 1, parentItem.itemId),
    );
    totalOptions += 1;
  });

  return {
    items,
    warnings,
    totalOptions,
  };
}